import { CameraControls, CameraControlsImpl } from "@react-three/drei";
import { type ThreeEvent, useFrame, useThree } from "@react-three/fiber";
import type React from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";
import { Box3, type Group, PerspectiveCamera, Vector3 } from "three";
import { ThreeToolsEvents, ThreeToolsInstance } from "../util/ThreeTools";
import { useProductStoreContext } from "./ProductStoreContext";
import {
  type CameraAutoFit,
  type CameraAutoFitTrigger,
  CameraControlsMode,
  useProductViewerConfig,
} from "./ProductViewerConfigContext";
import {
  areBoxesNearlyEqual,
  fitPerspectiveCameraToBox,
  hasPendingLoadingBounds,
  INITIAL_FIT_STABLE_FRAMES,
  ISOMETRIC_CAMERA_DIRECTION,
  waitForNextAnimationFrame,
} from "./productViewerCameraFit";
import { CameraView } from "./useProductViewer";

const CAMERA_TRANSITION_SMOOTH_TIME = 0.25;
const MIN_CAMERA_DIRECTION_LENGTH_SQ = 0.000001;

type CameraDirection = [number, number, number];

const CAMERA_VIEW_DIRECTION: Record<CameraView, CameraDirection> = {
  [CameraView.FRONT]: [0, 0, 2],
  [CameraView.BACK]: [0, 0, -2],
  [CameraView.TOP]: [0, 2, 0],
  [CameraView.BOTTOM]: [0, -2, 0],
  [CameraView.LEFT]: [-2, 0, 0],
  [CameraView.RIGHT]: [2, 0, 0],
  [CameraView.ISOMETRIC]: ISOMETRIC_CAMERA_DIRECTION,
};

const FREE_ORBIT_MOUSE_BUTTONS = {
  left: CameraControlsImpl.ACTION.ROTATE,
  middle: CameraControlsImpl.ACTION.DOLLY,
  right: CameraControlsImpl.ACTION.TRUCK,
  wheel: CameraControlsImpl.ACTION.DOLLY,
} as const;

const FIXED_ORBIT_MOUSE_BUTTONS = {
  left: CameraControlsImpl.ACTION.ROTATE,
  middle: CameraControlsImpl.ACTION.DOLLY,
  right: CameraControlsImpl.ACTION.NONE,
  wheel: CameraControlsImpl.ACTION.DOLLY,
} as const;

const FREE_ORBIT_TOUCHES = {
  one: CameraControlsImpl.ACTION.TOUCH_ROTATE,
  two: CameraControlsImpl.ACTION.TOUCH_DOLLY_TRUCK,
  three: CameraControlsImpl.ACTION.TOUCH_TRUCK,
} as const;

const FIXED_ORBIT_TOUCHES = {
  one: CameraControlsImpl.ACTION.TOUCH_ROTATE,
  two: CameraControlsImpl.ACTION.TOUCH_DOLLY,
  three: CameraControlsImpl.ACTION.NONE,
} as const;

const resetBoundsTracking = (
  stableFrameCountRef: React.RefObject<number>,
  lastBoxRef: React.RefObject<Box3 | null>,
) => {
  stableFrameCountRef.current = 0;
  lastBoxRef.current = null;
};

const hasStableBounds = (
  currentBox: Box3,
  stableFrameCountRef: React.RefObject<number>,
  lastBoxRef: React.RefObject<Box3 | null>,
) => {
  if (
    lastBoxRef.current &&
    areBoxesNearlyEqual(currentBox, lastBoxRef.current)
  ) {
    stableFrameCountRef.current += 1;
  } else {
    stableFrameCountRef.current = 1;
    lastBoxRef.current = currentBox.clone();
    return false;
  }

  return stableFrameCountRef.current >= INITIAL_FIT_STABLE_FRAMES;
};

const hasCameraAutoFitTrigger = (
  cameraAutoFit: CameraAutoFit,
  trigger: CameraAutoFitTrigger,
) => cameraAutoFit !== false && cameraAutoFit.includes(trigger);

interface CameraControlsContextValue {
  disableControls: () => (() => void) | undefined;
}

const CameraControlsContext = createContext<CameraControlsContextValue | null>(
  null,
);

export const useCameraControls = () => {
  const context = useContext(CameraControlsContext);
  return context;
};

interface ProductViewerCameraProps {
  children: React.ReactNode;
  onInitialFitComplete: () => void;
}

export const ProductViewerCamera = ({
  children,
  onInitialFitComplete,
}: ProductViewerCameraProps) => {
  const { cameraControls: cameraControlsMode, cameraAutoFit = false } =
    useProductViewerConfig();
  const meshRef = useRef<Group>(null);
  const cameraControlsRef = useRef<CameraControls>(null);
  const initialFitDoneRef = useRef(false);
  const stableBoundsFrameCountRef = useRef(0);
  const lastInitialFitBoxRef = useRef<Box3 | null>(null);
  const pendingAutoFitBuildVersionRef = useRef<number | null>(null);
  const stableAutoFitBoundsFrameCountRef = useRef(0);
  const lastAutoFitBoxRef = useRef<Box3 | null>(null);
  const previousBuildVersionRef = useRef(0);
  const previousCanvasSizeRef = useRef<{
    height: number;
    width: number;
  } | null>(null);
  const pendingCanvasResizeFitRef = useRef(false);
  const meshAssetsReadyRef = useRef(ThreeToolsInstance.numLoading === 0);
  const useProductStore = useProductStoreContext();
  const { setOnCameraViewChange, setOnCameraFit, setOnZoomIn, setOnZoomOut } =
    useProductStore();
  const buildVersion = useProductStore((state) => state.buildVersion);

  const { camera, size } = useThree();

  const autoFitOnConfigChange = hasCameraAutoFitTrigger(
    cameraAutoFit,
    "configChange",
  );
  const autoFitOnCanvasResize = hasCameraAutoFitTrigger(
    cameraAutoFit,
    "canvasResize",
  );

  const isFreeOrbit = cameraControlsMode === CameraControlsMode.FreeOrbit;
  const userInteractionEnabled = cameraControlsMode !== CameraControlsMode.None;

  useEffect(() => {
    const handleAllMeshesLoaded = () => {
      meshAssetsReadyRef.current = true;
    };

    ThreeToolsInstance.addEventListener(
      ThreeToolsEvents.ALL_DONE,
      handleAllMeshesLoaded,
    );

    return () => {
      ThreeToolsInstance.removeEventListener(
        ThreeToolsEvents.ALL_DONE,
        handleAllMeshesLoaded,
      );
    };
  }, []);

  useEffect(() => {
    if (cameraControlsRef.current) {
      cameraControlsRef.current.enabled = userInteractionEnabled;
    }
  }, [userInteractionEnabled]);

  useEffect(() => {
    const previousBuildVersion = previousBuildVersionRef.current;
    previousBuildVersionRef.current = buildVersion;

    if (
      !autoFitOnConfigChange ||
      buildVersion === 0 ||
      buildVersion === previousBuildVersion
    ) {
      return;
    }

    pendingAutoFitBuildVersionRef.current = buildVersion;
    stableAutoFitBoundsFrameCountRef.current = 0;
    lastAutoFitBoxRef.current = null;
  }, [autoFitOnConfigChange, buildVersion]);

  useEffect(() => {
    const previousCanvasSize = previousCanvasSizeRef.current;
    previousCanvasSizeRef.current = {
      height: size.height,
      width: size.width,
    };

    if (
      !autoFitOnCanvasResize ||
      !initialFitDoneRef.current ||
      !previousCanvasSize ||
      (previousCanvasSize.height === size.height &&
        previousCanvasSize.width === size.width)
    ) {
      return;
    }

    pendingCanvasResizeFitRef.current = true;
  }, [autoFitOnCanvasResize, size.height, size.width]);

  const getCurrentCameraViewDirection =
    useCallback((): CameraDirection | null => {
      const position = new Vector3();
      const target = new Vector3();

      camera.getWorldPosition(position);

      const controls = cameraControlsRef.current as unknown as {
        target?: Vector3;
        getTarget?: (target: Vector3) => void;
      };

      if (controls?.getTarget) {
        controls.getTarget(target);
      } else if (controls?.target) {
        target.copy(controls.target);
      } else {
        const forward = new Vector3();
        camera.getWorldDirection(forward);
        const direction = forward.negate().normalize();
        return [direction.x, direction.y, direction.z];
      }

      const direction = position.sub(target);
      if (direction.lengthSq() <= MIN_CAMERA_DIRECTION_LENGTH_SQ) {
        return null;
      }

      direction.normalize();
      return [direction.x, direction.y, direction.z];
    }, [camera]);

  // Store camera angles globally for PDF preview capture.
  const updateCameraAngles = useCallback(() => {
    if (!cameraControlsRef.current) {
      return;
    }

    try {
      const position = new Vector3();
      const target = new Vector3();

      camera.getWorldPosition(position);

      const controls = cameraControlsRef.current as unknown as {
        target?: Vector3;
        getTarget?: (target: Vector3) => void;
      };

      if (controls.getTarget) {
        controls.getTarget(target);
      } else if (controls.target) {
        target.copy(controls.target);
      } else {
        const direction = new Vector3();
        camera.getWorldDirection(direction);
        const estimatedDistance = 10;
        target.copy(position).sub(direction.multiplyScalar(estimatedDistance));
      }

      const direction = new Vector3().subVectors(position, target).normalize();
      const azimuth = Math.atan2(direction.x, direction.z);
      const polar = Math.acos(Math.max(-1, Math.min(1, direction.y)));

      (
        globalThis as unknown as {
          __r3fCameraAngles?: {
            azimuth: number;
            polar: number;
          };
        }
      ).__r3fCameraAngles = {
        azimuth,
        polar,
      };
    } catch {
      // Camera angles are only a best-effort handoff to preview capture.
    }
  }, [camera]);

  const fitCurrentCameraView = useCallback(
    async (enableTransition = true) => {
      if (
        !meshRef.current ||
        !cameraControlsRef.current ||
        !(camera instanceof PerspectiveCamera)
      ) {
        return;
      }

      const currentViewDirection = getCurrentCameraViewDirection();
      if (!currentViewDirection) {
        return;
      }

      meshRef.current.updateMatrixWorld(true);
      await fitPerspectiveCameraToBox(
        cameraControlsRef.current,
        camera,
        meshRef.current,
        currentViewDirection,
        enableTransition,
      );

      updateCameraAngles();
    },
    [camera, getCurrentCameraViewDirection, updateCameraAngles],
  );

  useFrame(() => {
    if (!(camera instanceof PerspectiveCamera)) return;
    if (!cameraControlsRef.current || !meshRef.current) return;

    if (ThreeToolsInstance.numLoading > 0) {
      meshAssetsReadyRef.current = false;
    }

    const needsInitialFit = !initialFitDoneRef.current;
    const hasPendingConfigAutoFit =
      initialFitDoneRef.current &&
      autoFitOnConfigChange &&
      pendingAutoFitBuildVersionRef.current !== null;
    const hasPendingCanvasResizeFit =
      initialFitDoneRef.current && pendingCanvasResizeFitRef.current;

    if (
      !needsInitialFit &&
      !hasPendingConfigAutoFit &&
      !hasPendingCanvasResizeFit
    ) {
      return;
    }

    meshRef.current.updateMatrixWorld(true);
    const hasPendingAssets =
      !meshAssetsReadyRef.current || hasPendingLoadingBounds(meshRef.current);

    if (needsInitialFit && hasPendingAssets) {
      resetBoundsTracking(stableBoundsFrameCountRef, lastInitialFitBoxRef);
      return;
    }

    if (
      (hasPendingConfigAutoFit || hasPendingCanvasResizeFit) &&
      hasPendingAssets
    ) {
      resetBoundsTracking(stableAutoFitBoundsFrameCountRef, lastAutoFitBoxRef);
      return;
    }

    if (hasPendingCanvasResizeFit && !hasPendingConfigAutoFit) {
      pendingCanvasResizeFitRef.current = false;
      void fitCurrentCameraView();
      return;
    }

    if (hasPendingConfigAutoFit) {
      const currentBox = new Box3().setFromObject(meshRef.current);
      if (currentBox.isEmpty()) return;
      if (
        !hasStableBounds(
          currentBox,
          stableAutoFitBoundsFrameCountRef,
          lastAutoFitBoxRef,
        )
      ) {
        return;
      }

      pendingAutoFitBuildVersionRef.current = null;
      pendingCanvasResizeFitRef.current = false;
      resetBoundsTracking(stableAutoFitBoundsFrameCountRef, lastAutoFitBoxRef);
      void fitCurrentCameraView();
      return;
    }

    const currentBox = new Box3().setFromObject(meshRef.current);
    if (currentBox.isEmpty()) return;

    if (
      !hasStableBounds(
        currentBox,
        stableBoundsFrameCountRef,
        lastInitialFitBoxRef,
      )
    ) {
      return;
    }

    initialFitDoneRef.current = true;
    void fitPerspectiveCameraToBox(
      cameraControlsRef.current,
      camera,
      meshRef.current,
      ISOMETRIC_CAMERA_DIRECTION,
      false,
    ).then(() => {
      updateCameraAngles();
      void waitForNextAnimationFrame()
        .then(waitForNextAnimationFrame)
        .then(onInitialFitComplete);
    });
  });

  useEffect(() => {
    if (!cameraControlsRef.current) return;

    const controls = cameraControlsRef.current;
    const handleChange = () => {
      updateCameraAngles();
    };

    controls.addEventListener("control", handleChange);
    updateCameraAngles();

    setOnCameraViewChange(async (view) => {
      if (
        !meshRef.current ||
        !cameraControlsRef.current ||
        !(camera instanceof PerspectiveCamera)
      ) {
        return;
      }

      await fitPerspectiveCameraToBox(
        cameraControlsRef.current,
        camera,
        meshRef.current,
        CAMERA_VIEW_DIRECTION[view],
      );

      updateCameraAngles();
    });

    setOnCameraFit(fitCurrentCameraView);

    setOnZoomIn(() => {
      if (cameraControlsRef.current) {
        cameraControlsRef.current.dolly(0.3, true);
      }
    });

    setOnZoomOut(() => {
      if (cameraControlsRef.current) {
        cameraControlsRef.current.dolly(-0.3, true);
      }
    });

    return () => {
      controls.removeEventListener("control", handleChange);
    };
  }, [
    camera,
    fitCurrentCameraView,
    setOnCameraViewChange,
    setOnCameraFit,
    setOnZoomIn,
    setOnZoomOut,
    updateCameraAngles,
  ]);

  const disableControls = useCallback(() => {
    if (cameraControlsRef.current) {
      cameraControlsRef.current.enabled = false;
    }

    return () => {
      if (cameraControlsRef.current) {
        cameraControlsRef.current.enabled = userInteractionEnabled;
      }
    };
  }, [userInteractionEnabled]);

  const handlePointerDown = useCallback(
    (evt: ThreeEvent<PointerEvent>) => {
      if (!isFreeOrbit) {
        return;
      }

      cameraControlsRef.current?.setOrbitPoint(
        evt.point.x,
        evt.point.y,
        evt.point.z,
      );
    },
    [isFreeOrbit],
  );

  const contextValue = useMemo(() => ({ disableControls }), [disableControls]);

  return (
    <CameraControlsContext.Provider value={contextValue}>
      <CameraControls
        ref={(instance) => {
          cameraControlsRef.current = instance;
          if (instance) {
            instance.enabled = userInteractionEnabled;
          }
        }}
        mouseButtons={
          isFreeOrbit ? FREE_ORBIT_MOUSE_BUTTONS : FIXED_ORBIT_MOUSE_BUTTONS
        }
        touches={isFreeOrbit ? FREE_ORBIT_TOUCHES : FIXED_ORBIT_TOUCHES}
        dollyToCursor={isFreeOrbit}
        smoothTime={CAMERA_TRANSITION_SMOOTH_TIME}
        draggingSmoothTime={0}
        dollySpeed={0.3}
      />
      <group ref={meshRef} visible={true} onPointerDown={handlePointerDown}>
        {children}
      </group>
    </CameraControlsContext.Provider>
  );
};
